/**
 * AdminLayout — chrome for the owner-only B2C admin at /admin/*.
 *
 * Distinct from AccountLayout (which is for paying customers): this
 * console is for Manivannan + future internal admins. Sidebar nav is
 * fixed-width so dense tables / charts fill the rest of the viewport.
 *
 * Auth: RequireSuperuser wraps each /admin/* route (router/index.jsx).
 * No need to re-check here — if we're rendering, the guard passed.
 *
 * Nav is grouped into sections (Operations / Cricket / Money / …) and
 * the section list scrolls inside the sidebar so the "Back to main
 * app" + Django admin footer stay visible on short laptops.
 *
 * On small screens (md-and-below) the sidebar becomes a drawer toggled
 * by a hamburger in the header — keeps full-width tables usable on
 * phones without losing nav.
 */
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Store,
  Wallet,
  Activity,
  ExternalLink,
  ArrowLeft,
  MessageSquare,
  MessageSquareWarning,
  Brain,
  BarChart3,
  AlertOctagon,
  Layers,
  Database,
  UserCheck,
  KeyRound,
  Sparkles,
  Receipt,
  Trophy,
  Globe,
  Cloud,
  Menu,
  X,
  FileSpreadsheet,
} from "lucide-react";
import useAuthStore from "../store/auth.store";
import ThemeToggle from "../components/common/ThemeToggle";
import ErrorBoundary from "../components/common/ErrorBoundary";
import { ToastHost } from "../components/common/ToastHost";
import KeyboardHelp from "../components/common/KeyboardHelp";
import VersionWatcher from "../components/common/VersionWatcher";
import AdminCreateMenu from "./AdminCreateMenu";
import AdminNotificationBell from "./AdminNotificationBell";

const NAV_SECTIONS = [
  {
    label: "Operations",
    items: [
      { to: "/admin",              label: "Dashboard",     icon: LayoutDashboard, end: true },
      { to: "/admin/users",        label: "Users",         icon: Users },
      { to: "/admin/organizations",label: "Organizations", icon: Building2 },
      { to: "/admin/access",       label: "Access",        icon: KeyRound },
    ],
  },
  {
    label: "Cricket",
    items: [
      { to: "/admin/matches",            label: "Matches",            icon: Trophy },
      { to: "/admin/marketplace",        label: "Marketplace",        icon: Store },
      { to: "/admin/scorer-marketplace", label: "Scorer marketplace", icon: UserCheck },
    ],
  },
  {
    label: "Money",
    items: [
      { to: "/admin/finances", label: "Finances", icon: Wallet },
      { to: "/admin/spend",    label: "AWS spend", icon: Cloud },
      { to: "/admin/billing",  label: "Billing",  icon: Receipt },
      { to: "/admin/activity", label: "Activity", icon: Activity },
    ],
  },
  {
    label: "Customer",
    items: [
      { to: "/admin/feedback",         label: "Feedback",        icon: MessageSquare },
      { to: "/admin/chatbot-quality",  label: "Chatbot quality", icon: MessageSquareWarning },
    ],
  },
  {
    label: "Data ops",
    items: [
      { to: "/admin/ingest/players",   label: "Player countries",   icon: Globe },
      { to: "/admin/ingest/review",    label: "Review queue",       icon: Layers },
      { to: "/admin/ingest/anomalies", label: "Anomalies",          icon: AlertOctagon },
      { to: "/admin/ingest/coverage",  label: "Coverage",           icon: BarChart3 },
      // 2026-07-09 — Opta CSV / Vendor XLSX → per-match ZCD workbooks.
      { to: "/admin/zcd-convert",      label: "ZCD conversion",     icon: FileSpreadsheet },
    ],
  },
  {
    label: "Growth",
    items: [
      { to: "/admin/leads",       label: "Leads (AI engine)", icon: Sparkles },
      { to: "/admin/ai-learning", label: "AI learning",       icon: Brain },
      { to: "/admin/ai-usage",    label: "AI usage",          icon: BarChart3 },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/database", label: "Database", icon: Database },
    ],
  },
];

export default function AdminLayout() {
  const user = useAuthStore((s) => s.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  // Escape closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e) { if (e.key === "Escape") setDrawerOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  return (
    <ToastHost>
    <div className="h-screen flex bg-bg-main text-text-primary overflow-hidden">
      {/* Mobile backdrop — fades the page, click dismisses */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
        />
      )}

      {/* Sidebar — pinned to viewport height so the nav scrolls
          internally without pushing the footer out of view. */}
      <aside
        aria-label="Owner Console navigation"
        className={`w-52 shrink-0 border-r border-border-default bg-bg-primary flex flex-col
                    fixed md:static inset-y-0 left-0 z-40 h-screen
                    transform transition-transform duration-200
                    ${drawerOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="px-3.5 py-3 border-b border-border-default flex items-center justify-between">
          <div>
            <div className="text-[9.5px] uppercase tracking-widest text-text-secondary">
              Owner Console
            </div>
            <div className="text-[13.5px] font-semibold mt-0.5">ZenCricketData</div>
          </div>
          {/* Close button — mobile only */}
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation"
            className="md:hidden p-1 rounded hover:bg-bg-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Primary CTA — single button, click opens a dropdown listing
            every creatable entity. No intermediate page. */}
        <div className="px-2.5 pt-2.5">
          <AdminCreateMenu />
        </div>

        <nav
          aria-label="Primary"
          className="flex-1 overflow-y-auto px-2 py-2.5 space-y-2"
        >
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <div className="px-2.5 pb-0.5 text-[9.5px] uppercase tracking-widest text-text-secondary">
                {section.label}
              </div>
              <div className="space-y-[1px]">
                {section.items.map(({ to, label, icon: Icon, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={closeDrawer}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] transition-colors ${
                        isActive
                          ? "bg-button-primary text-white"
                          : "text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
                      }`
                    }
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-2 py-2 border-t border-border-default space-y-[1px]">
          <NavLink
            to="/dashboard"
            onClick={closeDrawer}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-3 h-3 shrink-0" />
            <span className="truncate">Back to main app</span>
          </NavLink>
          <a
            href="https://api.zencricketdata.com/admin/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
          >
            <ExternalLink className="w-3 h-3 shrink-0" />
            <span className="truncate">Django admin</span>
          </a>
        </div>
      </aside>

      {/* Main column — h-screen + own overflow so the sidebar stays
          static while page content scrolls independently. */}
      <div className="flex-1 min-w-0 flex flex-col h-screen overflow-hidden">
        <header className="h-12 shrink-0 border-b border-border-default px-4 flex items-center gap-3 bg-bg-primary">
          {/* Hamburger — mobile only */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            className="md:hidden p-1.5 rounded hover:bg-bg-secondary"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-xs text-text-secondary ml-auto truncate">
            Signed in as <span className="text-text-primary">{user?.email}</span>
          </span>
          <AdminNotificationBell />
          <ThemeToggle />
        </header>
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-4">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <KeyboardHelp />
      <VersionWatcher />
    </div>
    </ToastHost>
  );
}
